import _ from "lodash";
import api from "@/api/cloud";
import { get, set } from "@/utils/vuex";
import { isoTz, parseDate, fromNow, endOf } from "@/utils/dateHelpers";

const LS_KEY = "alerts";
const PENDING_KEY = "alerts.pending";
const OLDER_KEY = "alerts.older";

const apiDate = (date) => {
	date = isoTz(date);
	return isNaN(date.valueOf()) ? null : encodeURIComponent(date);
};

const dateGrouper = (alert) => {
	const date = parseDate(alert.ts).toISOString();
	return fromNow({ hours: 1 }) < date
		? "Pending"
		: fromNow({ days: 30 }) > date
		? "Older"
		: "Recent";
};

const state = () => ({
	alerts: [],
	history: [],
	current: null,
	loading: false,
	alertsCount: 0,
	historyCount: 0,
	alertsSummary: null,
	alertsDebouncer: null,
	simpleStatus: {
		pending: 0,
		showPending: false,
		older: 0,
		showOlder: false,
	},
	query: {
		controllerName: null,
		alarmType: ["Controller-connection", "Device-connection"],
		communityIds: null,
		alarmStatus: ["ACKNOWLEDGED", "ACTIVE"],
		startDate: null,
		endDate: null,
		sort: "DESC",
		limit: 20,
		page: 0,
	},
});

const getters = {
	alerts: get("alerts"),
	history: get("history"),
	current: get("current"),
	loading: get("loading"),
	query: get("query"),
	count: get("alertsCount"),
	historyCount: get("historyCount"),
	summary: get("alertsSummary"),
	limit: (state) => state.query.limit,
	page: (state) => state.query.page + 1,

	activeAlerts(s, getters) {
		const latestByType = _.groupBy(getters.alerts, dateGrouper);
		return _.mapValues(latestByType, (alarm) => _.groupBy(alarm, "alarm_type"));
	},

	pending: get("simpleStatus.pending"),
	showPending: get("simpleStatus.showPending"),
	older: get("simpleStatus.older"),
	showOlder: get("simpleStatus.showOlder"),
	allowSimpleStatus: (state) =>
		!state.query.alarmStatus.length ||
		state.query.alarmStatus.some((status) =>
			["ACTIVE", "ACKNOWLEDGED"].includes(status)
		),
	pendingFilter: (state, getters) => () =>
		!getters.allowSimpleStatus || state.simpleStatus.showPending
			? null
			: fromNow({ hours: 1 }),
	olderFilter: (state, getters) => () =>
		!getters.allowSimpleStatus || state.simpleStatus.showOlder
			? null
			: fromNow({ days: 30 }),
};

const mutations = {
	SET_ALERTS: set("alerts"),
	SET_LOADING: set("loading"),
	SET_DEBOUNCER: set("alertsDebouncer"),
	SET_HISTORY: set("history"),
	SET_CURRENT: set("current"),
	SET_COUNT: set("alertsCount"),
	SET_HISTORY_COUNT: set("historyCount"),
	SET_SUMMARY: set("alertsSummary"),

	CLEAR(currentState) {
		Object.assign(currentState, state());
	},

	resetFilters(currentState) {
		Object.assign(currentState.query, state().query);
	},

	saveFilters(state, _query) {
		const { page, ...query } = Object.assign(state.query, _query);
		state.query.page = page;
		localStorage.setItem(LS_KEY, JSON.stringify(query));
	},

	setShowPending(state, status) {
		state.simpleStatus.showPending = status;
		localStorage.setItem(PENDING_KEY, status);
	},

	setShowOlder(state, status) {
		state.simpleStatus.showOlder = status;
		localStorage.setItem(OLDER_KEY, status);
	},

	setPending: set("simpleStatus.pending"),
	setOlder: set("simpleStatus.older"),
};

const actions = {
	loadFilters({ commit, dispatch, rootGetters }) {
		try {
			let query = JSON.parse(localStorage.getItem(LS_KEY));
			const showPending = localStorage.getItem(PENDING_KEY) === "true";
			const showOlder = localStorage.getItem(OLDER_KEY) === "true";

			if (!query) {
				let defaultCommunity =
					_.get(rootGetters["auth/user"], "properties.defaultCommunity") ||
					_.get(rootGetters["auth/communities"], "0.id");

				query = {
					communityIds:
						typeof defaultCommunity === "string"
							? Number(defaultCommunity)
							: defaultCommunity,
				};
			}

			commit("saveFilters", query);
			commit("setShowPending", showPending);
			commit("setShowOlder", showOlder);
		} catch {
			localStorage.removeItem(LS_KEY);
			dispatch("init");
		}
	},

	resetFilters({ commit }) {
		localStorage.removeItem(LS_KEY);
		commit("setShowPending", false);
		commit("setShowOlder", false);
		commit("resetFilters");
	},

	changeQuery({ commit, dispatch, state }, { reset = false, ...newQuery }) {
		const oldQuery = state.query;
		const query = { ...oldQuery, ...newQuery };

		if (reset) query.page = 0;

		if (!_.isEqual(query, oldQuery)) {
			commit("saveFilters", query);
			dispatch("debounceAlerts");
		}
	},

	async debounceAlerts({ commit, dispatch, state, getters }) {
		commit("SET_LOADING", true);
		if (!state.alertsDebouncer) {
			const debouncer = _.debounce(
				async () => {
					try {
						const query = getters["query"];
						await dispatch("getAlerts", query);
					} finally {
						commit("SET_LOADING", false);
					}
				},
				1000,
				{ maxWait: 5000, trailing: true }
			);
			commit("SET_DEBOUNCER", debouncer);
		}
		state.alertsDebouncer();
	},

	async getAlerts({ getters, commit }, query) {
		const setPanels = query.startDate || query.endDate ? 0 : true;
		const startDate = apiDate(query.startDate || getters.olderFilter());
		const endDate = apiDate(
			query.endDate ? endOf(query.endDate) : getters.pendingFilter()
		);

		const res = await api.getControllerAlerts(
			_.omitBy({ ...query, startDate, endDate }, _.isNil)
		);
		const { alarms, count, summary } = res;
		commit("SET_ALERTS", alarms);
		commit("SET_COUNT", count);
		commit("SET_SUMMARY", summary);
		commit("setPending", setPanels && summary.open_alarms.pending);
		commit("setOlder", setPanels && summary.open_alarms.older);
		return res;
	},

	async getActiveAlerts({ commit }, sysId) {
		const { alarms, count } = await api.getActiveAlerts(sysId);

		commit("SET_ALERTS", alarms);
		commit("SET_COUNT", count);
		return alarms;
	},

	async getAlertHistory({ commit }, { sysId, ...query }) {
		const startDate = apiDate(query.startDate);
		const endDate = apiDate(query.endDate && endOf(query.endDate));
		const { history, count } = await api.getAlertHistory(
			sysId,
			_.omitBy({ ...query, startDate, endDate }, _.isNil)
		);

		commit("SET_HISTORY", history);
		commit("SET_HISTORY_COUNT", count);
		return history;
	},

	async getSystem(ctx, sysId) {
		return api.getSystem(sysId);
	},

	async changeStatus(ctx, payload) {
		return api.changeAlertStatus(payload);
	},

	async reenable(ctx, payload) {
		return api.reenableAlert(payload);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
